// Conquistas permanentes: definições e regras puras de progresso.
// O servidor guarda o progresso (tabela achievements) e chama estas funções
// depois de cada partida e quando a pessoa abre a tela de conquistas.

export type AchievementCategory = "jornada" | "colecao" | "habilidade" | "secreta";

export interface AchievementReward {
  pokedollars?: number;
  /** título exibido sob o apelido */
  title?: string;
  /** moldura da foto de perfil */
  frame?: string;
}

export interface AchievementDef {
  id: string;
  category: AchievementCategory;
  /** valor a alcançar (contadores) ou 1 (feitos de uma partida) */
  goal: number;
  /** de onde vem o progresso */
  metric: AccountMetric | MatchFeat;
  reward: AchievementReward;
  /** escondida até ser desbloqueada */
  secret?: boolean;
}

/** Contadores da conta (medidos no banco). */
export type AccountMetric =
  | "matches" | "wins" | "top4" | "rankedWins" | "bestStreak" | "missions" | "level" | "unlockedLines" | "shinies" | "cards" | "masterball";

/** Feitos dentro de uma partida (medidos no fim dela). */
export type MatchFeat =
  | "feat:agua7" | "feat:voador7" | "feat:brigao6" | "feat:muralha6" | "feat:three3" | "feat:winLowHp" | "feat:level9"
  | "feat:items6" | "feat:magikarp3" | "feat:onlyCost1Win" | "feat:noPvpWinTop4" | "feat:solo87" | "feat:dragonite3" | "feat:mewtwo";

export const ACHIEVEMENTS: AchievementDef[] = [
  // ---------------------------------------------------------------- jornada
  { id: "primeira-partida", category: "jornada", metric: "matches", goal: 1, reward: { pokedollars: 50 } },
  { id: "partidas-10", category: "jornada", metric: "matches", goal: 10, reward: { pokedollars: 100 } },
  { id: "partidas-100", category: "jornada", metric: "matches", goal: 100, reward: { pokedollars: 300, title: "Veterano" } },
  { id: "partidas-1000", category: "jornada", metric: "matches", goal: 1000, reward: { pokedollars: 1000, title: "Lenda de Kanto", frame: "ouro" } },
  { id: "primeira-vitoria", category: "jornada", metric: "wins", goal: 1, reward: { pokedollars: 100 } },
  { id: "vitorias-10", category: "jornada", metric: "wins", goal: 10, reward: { pokedollars: 250, title: "Campeão" } },
  { id: "vitorias-50", category: "jornada", metric: "wins", goal: 50, reward: { pokedollars: 600, frame: "prata" } },
  { id: "top4-25", category: "jornada", metric: "top4", goal: 25, reward: { pokedollars: 200 } },
  { id: "ranqueada-vitorias-10", category: "jornada", metric: "rankedWins", goal: 10, reward: { pokedollars: 400, title: "Ás da Liga" } },
  { id: "master-ball", category: "jornada", metric: "masterball", goal: 1, reward: { pokedollars: 1500, title: "Mestre Pokémon", frame: "master" } },
  { id: "dias-7", category: "jornada", metric: "bestStreak", goal: 7, reward: { pokedollars: 150 } },
  { id: "dias-30", category: "jornada", metric: "bestStreak", goal: 30, reward: { pokedollars: 500, title: "Fiel" } },
  { id: "missoes-50", category: "jornada", metric: "missions", goal: 50, reward: { pokedollars: 300 } },
  { id: "nivel-20", category: "jornada", metric: "level", goal: 20, reward: { pokedollars: 300 } },
  // ---------------------------------------------------------------- coleção
  { id: "criadouro-1", category: "colecao", metric: "unlockedLines", goal: 1, reward: { pokedollars: 100 } },
  { id: "pokedex-completa", category: "colecao", metric: "unlockedLines", goal: 22, reward: { pokedollars: 1000, title: "Professor", frame: "pokedex" } },
  { id: "shinies-5", category: "colecao", metric: "shinies", goal: 5, reward: { pokedollars: 200 } },
  { id: "shinies-25", category: "colecao", metric: "shinies", goal: 25, reward: { pokedollars: 600, title: "Caçador de Shinies", frame: "shiny" } },
  { id: "cartas-10", category: "colecao", metric: "cards", goal: 10, reward: { pokedollars: 300 } },
  // ---------------------------------------------------------------- habilidade
  { id: "agua-7", category: "habilidade", metric: "feat:agua7", goal: 1, reward: { pokedollars: 150, title: "Mestre das Águas" } },
  { id: "voador-7", category: "habilidade", metric: "feat:voador7", goal: 1, reward: { pokedollars: 150 } },
  { id: "brigao-6", category: "habilidade", metric: "feat:brigao6", goal: 1, reward: { pokedollars: 150 } },
  { id: "muralha-6", category: "habilidade", metric: "feat:muralha6", goal: 1, reward: { pokedollars: 150, title: "Inabalável" } },
  { id: "tres-estrelas-3", category: "habilidade", metric: "feat:three3", goal: 1, reward: { pokedollars: 300, title: "Estrelado" } },
  { id: "vitoria-por-um-fio", category: "habilidade", metric: "feat:winLowHp", goal: 1, reward: { pokedollars: 200, title: "Por um Fio" } },
  { id: "nivel-9", category: "habilidade", metric: "feat:level9", goal: 1, reward: { pokedollars: 100 } },
  { id: "itens-6", category: "habilidade", metric: "feat:items6", goal: 1, reward: { pokedollars: 150 } },
  { id: "giovanni", category: "habilidade", metric: "feat:solo87", goal: 1, reward: { pokedollars: 300, title: "Derrotou Giovanni" } },
  { id: "dragonite-3", category: "habilidade", metric: "feat:dragonite3", goal: 1, reward: { pokedollars: 300 } },
  // ---------------------------------------------------------------- secretas
  { id: "magikarp-3", category: "secreta", secret: true, metric: "feat:magikarp3", goal: 1, reward: { pokedollars: 250, title: "Splash!" } },
  { id: "so-custo-1", category: "secreta", secret: true, metric: "feat:onlyCost1Win", goal: 1, reward: { pokedollars: 400, title: "Humilde" } },
  { id: "sem-vitoria-top4", category: "secreta", secret: true, metric: "feat:noPvpWinTop4", goal: 1, reward: { pokedollars: 200, title: "Sobrevivente" } },
  { id: "mewtwo", category: "secreta", secret: true, metric: "feat:mewtwo", goal: 1, reward: { pokedollars: 200 } },
];

export const ACHIEVEMENT_BY_ID = new Map(ACHIEVEMENTS.map((a) => [a.id, a]));

/** O que uma partida rendeu para as conquistas de habilidade (montado no fim da partida). */
export interface MatchFeats {
  won: boolean;
  placement: number;
  players: number;
  finalHp: number;
  soloCleared: boolean;
  peakTraits: Record<string, number>;
  maxLevel: number;
  readyItems: number;
  /** maior número de ★3 ao mesmo tempo no tabuleiro */
  star3Max: number;
  /** linhas que chegaram a ★3 */
  star3Units: string[];
  /** maior custo que já entrou em combate */
  maxBoardCost: number;
  pvpWins: number;
  pvpLosses: number;
  /** unidades que já entraram em combate */
  fieldedUnits: string[];
}

export function featsMet(f: MatchFeats): Set<MatchFeat> {
  const out = new Set<MatchFeat>();
  if ((f.peakTraits.agua ?? 0) >= 7) out.add("feat:agua7");
  if ((f.peakTraits.voador ?? 0) >= 7) out.add("feat:voador7");
  if ((f.peakTraits.brigao ?? 0) >= 6) out.add("feat:brigao6");
  if ((f.peakTraits.muralha ?? 0) >= 6) out.add("feat:muralha6");
  if (f.star3Max >= 3) out.add("feat:three3");
  if (f.won && f.players > 1 && f.finalHp > 0 && f.finalHp <= 5) out.add("feat:winLowHp");
  if (f.maxLevel >= 9) out.add("feat:level9");
  if (f.readyItems >= 6) out.add("feat:items6");
  if (f.star3Units.includes("magikarp")) out.add("feat:magikarp3");
  if (f.star3Units.includes("dratini")) out.add("feat:dragonite3");
  if (f.won && f.players >= 4 && f.maxBoardCost <= 1) out.add("feat:onlyCost1Win");
  if (f.players >= 4 && f.placement <= 4 && f.pvpWins === 0 && f.pvpLosses > 0) out.add("feat:noPvpWinTop4");
  if (f.soloCleared) out.add("feat:solo87");
  if (f.fieldedUnits.includes("mewtwo")) out.add("feat:mewtwo");
  return out;
}

/** Progresso de uma conquista dado os contadores da conta e os feitos já alcançados. */
export function achievementProgress(a: AchievementDef, account: Partial<Record<AccountMetric, number>>, feats: Set<MatchFeat>): number {
  if (a.metric.startsWith("feat:")) return feats.has(a.metric as MatchFeat) ? 1 : 0;
  return Math.min(a.goal, account[a.metric as AccountMetric] ?? 0);
}
